/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import {
  WebsocketInitialProperties,
  WebsocketListingProperties,
  WebsocketListingRequest,
  WebsocketListingResponse,
} from '../../interface/blocked-neverblocked.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ConfigureListFilterResponse,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import device from '../../utils/api/device';

export const fetchErrorLogs = createAsyncThunk<WebsocketListingResponse, WebsocketListingRequest>(
  'error-logs/fetchErrorLogs',
  async (data: WebsocketListingRequest, { dispatch }) => {
    try {
      const response = await device.fetchErrorLogs(data);
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          CONNREFUSED: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

export const fetchErrorLogsFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'error-logs/fetchErrorLogsFilters',
  async (_, { dispatch }) => {
    try {
      const response = await device.fetchErrorLogs();
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);
const Filter: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];
const initialState: WebsocketInitialProperties = {
  devices: [],
  limit: 0,
  page: 0,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  selectedChargingPoint: null,
  deviceListLoading: false,
  filters: Filter,
  searchBy: '',
  sortBy: '',
  sortField: '',
};

export const errorLogs = createSlice({
  name: 'error-logs',
  initialState,
  reducers: {
    /**
     * Function to update selectedChargingPoint
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing the selected device.
     */
    setSelectedDevice: (state, action) => {
      state.selectedChargingPoint = action.payload;
    },
    /**
     * Function to update filters for charging points
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing filter updates.
     */
    updateErrorLogsFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter } = UpdateCommonFilter(state.filters, action.payload);
        state.filters = updatedCommonFilter;
      }
    },
    /**
     * setVlbcSearchBy function to update the searchBy property in vlbcs state
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing the search query.
     */
    setVlbcSearchBy: (state, action) => {
      state.searchBy = action.payload;
    },
    /**
     * function for set sortBy
     * @param {any} state The current slice state.
     * @param {any} action The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder The action reducer map builder.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchErrorLogs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchErrorLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.devices = action.payload?.results?.length
          ? action.payload.results?.map((eachDevice: WebsocketListingProperties) => ({
              ...eachDevice,
              id: eachDevice?._id,
              connector1: eachDevice?.connectorsStatus?.connector1 || '',
              connector2: eachDevice?.connectorsStatus?.connector2 || '',
              blockUntil: ConvertUTCtoDate(eachDevice.blockUntil || ''),
            }))
          : [];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchErrorLogs.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
  },
});

// Export the actions
export const { setVlbcSearchBy, setSelectedDevice, setSortData, updateErrorLogsFilters } =
  errorLogs.actions;
// Export the reducer
export default errorLogs.reducer;
